/**
 * LeaveOnBehalfDialog — meerdaags verlof toevoegen namens een medewerker.
 *
 * Loopt via de aanvraag-flow (leave-requests?action=create): direct approved
 * met reviewer = ingelogde gebruiker, entries via hetzelfde pad als een
 * goedgekeurde aanvraag. Uren per dag komen server-side uit het gedeelde
 * verlof-primitief (netto rooster-uren); de preview gebruikt dry-run zodat
 * UI en afboeking per constructie dezelfde cijfers tonen.
 */

import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useWorkforceStore, apiFetch } from '../../stores/workforce-store';
import type { Staff, LeaveType, LeaveEntitlement, OnBehalfResult } from '../../stores/workforce-store';
import { LEAVE_TYPES } from '../../lib/leave-types';
import { formatHours } from '../../lib/hours-calculator';

interface LeaveOnBehalfDialogProps {
  staffList: Staff[];
  preselectedStaffId?: string;
  onClose: () => void;
  onCreated?: () => void;
}

export function LeaveOnBehalfDialog({ staffList, preselectedStaffId, onClose, onCreated }: LeaveOnBehalfDialogProps) {
  const { createLeaveOnBehalf, lastMutationError } = useWorkforceStore();

  const selectableStaff = useMemo(
    () => staffList.filter(s => s.is_active && s.contract_type !== 'zzp' && s.contract_type !== 'dga'),
    [staffList]
  );

  const today = format(new Date(), 'yyyy-MM-dd');
  const [staffId, setStaffId] = useState(preselectedStaffId || '');
  const [leaveType, setLeaveType] = useState<LeaveType>('vakantie');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState<OnBehalfResult | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Potje-impact: entitlements + opgenomen uren van de gekozen medewerker
  const [entitlements, setEntitlements] = useState<LeaveEntitlement[]>([]);
  const [usedHours, setUsedHours] = useState<number>(0);

  const matchingEntitlement = useMemo(
    () => entitlements.find(ent => ent.leave_type === leaveType && ent.valid_until >= today),
    [entitlements, leaveType, today]
  );

  // Dry-run preview zodra staff + range compleet zijn
  useEffect(() => {
    if (!staffId || !startDate || !endDate || endDate < startDate) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setIsLoadingPreview(true);
    createLeaveOnBehalf({ staff_id: staffId, leave_type: leaveType, start_date: startDate, end_date: endDate, dry_run: true })
      .then(result => { if (!cancelled) setPreview(result); })
      .finally(() => { if (!cancelled) setIsLoadingPreview(false); });
    return () => { cancelled = true; };
  }, [staffId, leaveType, startDate, endDate, createLeaveOnBehalf]);

  // Potje-gegevens bij staff-wissel
  useEffect(() => {
    if (!staffId) {
      setEntitlements([]);
      setUsedHours(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ents: LeaveEntitlement[] = await apiFetch(`/api/workforce/leave-entitlements?staff_id=${staffId}`);
        if (cancelled) return;
        setEntitlements(ents || []);
      } catch { /* potje-info is nice-to-have */ }
    })();
    return () => { cancelled = true; };
  }, [staffId]);

  // Opgenomen uren binnen het geldige potje-venster
  useEffect(() => {
    if (!staffId || !matchingEntitlement) {
      setUsedHours(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const entries: { leave_type: string; hours: number }[] = await apiFetch(
          `/api/workforce/leave?staff_id=${staffId}&from=${matchingEntitlement.valid_from}&to=${matchingEntitlement.valid_until}`
        );
        if (cancelled) return;
        setUsedHours(
          (entries || [])
            .filter(e => e.leave_type === matchingEntitlement.leave_type)
            .reduce((sum, e) => sum + e.hours, 0)
        );
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [staffId, matchingEntitlement]);

  const totalPreviewHours = preview?.total_hours ?? 0;
  const remaining = matchingEntitlement ? matchingEntitlement.total_hours - usedHours : null;
  const remainingAfter = remaining !== null ? remaining - totalPreviewHours : null;

  const handleSave = async () => {
    if (!staffId || !preview || preview.days.length === 0) return;
    setIsSaving(true);
    setError(null);
    const result = await createLeaveOnBehalf({
      staff_id: staffId,
      leave_type: leaveType,
      start_date: startDate,
      end_date: endDate,
      notes: notes || undefined,
    });
    setIsSaving(false);
    if (result?.success) {
      onCreated?.();
      onClose();
    } else {
      const body = useWorkforceStore.getState().lastMutationError;
      setError(body?.message || lastMutationError?.message || 'Opslaan mislukt');
    }
  };

  const staffName = selectableStaff.find(s => s.id === staffId)?.name;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-main mb-1">Verlof toevoegen</h3>
        <p className="text-sm text-muted mb-5">
          Namens een medewerker, direct goedgekeurd. Uren per dag volgen het werkrooster (netto, na pauze-aftrek).
        </p>

        {/* Staff picker */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-muted mb-1">Medewerker</label>
          <select
            value={staffId}
            onChange={e => setStaffId(e.target.value)}
            disabled={!!preselectedStaffId}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none disabled:bg-slate-50"
          >
            <option value="">— Kies medewerker —</option>
            {selectableStaff.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        {/* Leave type */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {LEAVE_TYPES.map(lt => (
            <button
              key={lt.value}
              onClick={() => setLeaveType(lt.value)}
              className={`py-2 rounded-lg text-xs font-medium border transition-colors ${
                leaveType === lt.value ? lt.color : 'bg-white border-gray-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {lt.label}
            </button>
          ))}
        </div>

        {/* Date range */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Van</label>
            <input
              type="date"
              value={startDate}
              onChange={e => {
                setStartDate(e.target.value);
                if (e.target.value > endDate) setEndDate(e.target.value);
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Tot en met</label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={e => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        {/* Potje-impact */}
        {matchingEntitlement && remaining !== null && (
          <div className={`mb-4 p-3 rounded-lg border text-sm ${
            (remainingAfter ?? 0) >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <span className="font-medium">Potje: {matchingEntitlement.description || matchingEntitlement.leave_type}</span>
              <span className="font-semibold">
                {formatHours(remaining)} → {remainingAfter !== null ? formatHours(remainingAfter) : '-'}
              </span>
            </div>
            {remainingAfter !== null && remainingAfter < 0 && (
              <p className="text-xs text-red-600 font-medium mt-1">
                Overschrijdt het potje met {formatHours(Math.abs(remainingAfter))}
              </p>
            )}
          </div>
        )}

        {/* Per-dag preview (server dry-run: zelfde cijfers als de afboeking) */}
        {isLoadingPreview && <p className="text-xs text-muted mb-4">Uren berekenen...</p>}
        {!isLoadingPreview && preview && preview.days.length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs font-medium text-muted mb-2">
              {preview.days.length} {preview.days.length === 1 ? 'dag' : 'dagen'} · totaal {formatHours(preview.total_hours)}
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {preview.days.map(d => (
                <div key={d.date} className="flex justify-between text-xs">
                  <span className="text-muted capitalize">{format(new Date(d.date + 'T00:00:00'), 'EEE d MMM', { locale: nl })}</span>
                  <span className="text-main font-medium">{formatHours(d.hours)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {!isLoadingPreview && staffId && preview && preview.days.length === 0 && (
          <p className="text-xs text-amber-600 mb-4">Geen roosterdagen in deze periode — er wordt niets afgeboekt.</p>
        )}

        {/* Notes */}
        <div className="mb-5">
          <label className="block text-xs font-medium text-muted mb-1">Notitie</label>
          <input
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Optioneel"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
          />
        </div>

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={isSaving || isLoadingPreview || !staffId || !preview || preview.days.length === 0}
            className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {isSaving
              ? 'Opslaan...'
              : `Toevoegen${staffName ? ` voor ${staffName.split(' ')[0]}` : ''}${preview && preview.days.length > 0 ? ` (${formatHours(preview.total_hours)})` : ''}`}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-slate-600 hover:bg-slate-100 rounded-lg text-sm transition-colors"
          >
            Annuleren
          </button>
        </div>
      </div>
    </div>
  );
}
